import { resolveApiOrigin } from '../api/api-origin.js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getActiveTenantId, setActiveTenantId } from './active-tenant.js';
import { getActiveScopePath, setActiveScopePath } from './active-scope.js';
import { applyBrand } from './brand.js';
import { isAllowedPortalOrigin, isDevBuild } from './portal-origins.js';
import { isTrustedPortalMessage } from './portal-message.js';

/**
 * AuthBootstrap — resolves the session token before the rest of the app
 * renders.
 *
 * Three sources, in order (see ARCHITECTURE.md §8 Integration):
 *
 *   1. `postMessage` from the Canopy Angular portal host. This is the
 *      production path: the portal posts `{ type: 'canopy.auth', jwt,
 *      tenantId, userId, theme }` when the iframe loads and on every
 *      token refresh.
 *
 *   2. URL fragment `#token=...` — a fallback for navigation flows
 *      (deep links from email, for example) where postMessage isn't
 *      available immediately.
 *
 *   3. A dev token from localStorage, set by the developer during local
 *      work. Rejected server-side outside `NODE_ENV=development`.
 *
 * The token is held in memory only and exposed through the `AuthSession`
 * context. Never persisted to storage beyond the dev-only shim.
 */

export interface AuthSession {
  readonly token: string;
  readonly source: 'postMessage' | 'urlFragment' | 'devToken';
  /**
   * Refreshes the token — wired to the portal via postMessage in prod.
   *
   * CSAGE-387: this had ZERO callers from the day it was written. The API
   * client now calls it on a 401 (see `client.ts`), and a successful refresh
   * also lands the new credential on the live session, so the retry and every
   * subsequent request use it. Resolving does NOT guarantee a different token:
   * non-portal sources have no refresh and return the one they were issued.
   */
  refresh(): Promise<string>;
}

const AuthSessionContext = createContext<AuthSession | null>(null);

export function useAuthSession(): AuthSession {
  const session = useContext(AuthSessionContext);
  if (!session) {
    throw new Error('useAuthSession must be used inside <AuthBootstrap>');
  }
  return session;
}

interface AuthBootstrapProps {
  readonly children: ReactNode;
  /** CSAGE-429: called when a genuinely NEW token is adopted mid-session —
   *  the host should refetch queries made under the old one. */
  readonly onTokenChanged?: () => void;
}

/**
 * CSAGE-387: the ready state holds the raw token + source rather than a built
 * `AuthSession`, and the session is derived from it. That is what makes the
 * credential replaceable: before this, `session` was built once inside the
 * bootstrap effect and there was no code path that could ever produce a second
 * one, so a tab's token was frozen at page load and every call made after its
 * expiry 401'd with `auth.invalid_token` until the user reloaded.
 */
type BootstrapState =
  | { status: 'resolving' }
  | { status: 'ready'; token: string; source: AuthSession['source'] }
  | { status: 'error'; devTokens: boolean };

export function AuthBootstrap({ children, onTokenChanged }: AuthBootstrapProps): JSX.Element {
  const [state, setState] = useState<BootstrapState>({ status: 'resolving' });

  /**
   * CSAGE-387: land a refreshed credential on the live session.
   *
   * Two callers, and the PULL is the load-bearing one — verified against
   * canopy-portal `insights-host.component.ts`:
   *
   *   * `AuthSession.refresh()` after a 401 (client.ts). The portal answers
   *     `canopy.auth.refresh.request` with a fresh `canopy.auth`, and the jwt
   *     it sends is genuinely fresh: `getCanopyToken()` reads the cookie that
   *     `CanopyRenewTokenTimeoutService` rewrites via `jwtService.renew()` 5
   *     minutes before expiry on a 1-minute interval. This is what makes the
   *     replay in `sendWithRefresh` see a DIFFERENT token and actually retry.
   *
   *   * the long-lived portal listener. The portal does NOT push on token
   *     renewal — nothing wires `refreshCanopyToken` to the Insights host, and
   *     `app.component.ts`'s `refreshCanopyToken$` field is declared and never
   *     assigned. `postAuth()` fires on exactly three things: Sage's
   *     `canopy.auth.ready`, Sage's `canopy.auth.refresh.request`, and an
   *     org-tree change. So this path lands the fresh jwt that rides along on
   *     an org switch — which Sage used to discard — and the reply to our own
   *     refresh request. It would not, on its own, have fixed the expiry bug.
   *
   * Identity-stable (`[]` deps) so it can be handed to `buildSession` without
   * making the derived session churn on every render. An unchanged or empty
   * jwt returns `prev` untouched — the portal re-posts `canopy.auth` for scope
   * and brand changes too, and re-deriving the session on those would make the
   * `useMemo` below emit a new object for no reason, refetching the world.
   */
  const adoptToken = useCallback(
    (jwt: string): void => {
      setState((prev) => {
        if (prev.status !== 'ready' || !jwt || jwt === prev.token) return prev;
        // CSAGE-429: a GENUINELY new token (late portal handshake, refresh,
        // org switch) means everything fetched under the old one may be
        // wrong or empty — tell the host to refetch. Unchanged tokens
        // return `prev` above, so this never fires on the portal's
        // cosmetic re-posts.
        onTokenChanged?.();
        return { ...prev, token: jwt };
      });
    },
    [onTokenChanged],
  );

  const session = useMemo(
    () => (state.status === 'ready' ? buildSession(state.token, state.source, adoptToken) : null),
    [state, adoptToken],
  );

  useEffect(() => {
    let cancelled = false;

    const resolve = async (): Promise<void> => {
      // 1) Listen for postMessage from the portal.
      //
      // CSAGE-429: when we are EMBEDDED (an iframe under the portal), the
      // portal WILL post auth — but a cold Angular boot routinely takes
      // longer than the old 500ms deadline, so first loads fell through to
      // a stale fragment token (or none) and every list rendered empty
      // until a manual refresh. Embedded → wait generously; standalone
      // (dev) keeps the fast fallback to fragment/dev tokens.
      const embedded = window.self !== window.top;
      const viaPortal = await waitForPortalAuth(embedded ? 10_000 : 500);
      if (cancelled) return;
      if (viaPortal) {
        setState({ status: 'ready', token: viaPortal, source: 'postMessage' });
        return;
      }

      // 2) URL fragment
      const fragmentToken = readFragmentToken();
      if (fragmentToken) {
        setState({ status: 'ready', token: fragmentToken, source: 'urlFragment' });
        return;
      }

      // 3) Dev token
      const devToken = localStorage.getItem('canopy.dev.token');
      if (devToken) {
        setState({ status: 'ready', token: devToken, source: 'devToken' });
        return;
      }

      // No token. Only offer the dev-token snippet where the API would accept
      // one; everywhere else (prod) the way in is the Canopy portal.
      const devTokens = await offerDevTokenSnippet();
      if (cancelled) return;
      setState({ status: 'error', devTokens });
    };

    void resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'resolving') {
    return (
      <div className="auth-bootstrap">
        <div className="auth-bootstrap-card">
          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <div className="brand-mark" aria-hidden="true">
              C
            </div>
            <div>
              <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Canopy Sage</h2>
              <p className="text-dim text-sm" style={{ margin: '2px 0 0' }}>
                Resolving your session…
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (state.status === 'error') return <SessionUnavailable devTokens={state.devTokens} />;
  // `session` is non-null whenever state.status === 'ready' (same useMemo
  // condition); the guard is for the type narrowing, not a reachable branch.
  if (!session) return <></>;
  return (
    <AuthSessionContext.Provider value={session}>
      <ActiveTenantResolver token={session.token} onToken={adoptToken}>
        {children}
      </ActiveTenantResolver>
    </AuthSessionContext.Provider>
  );
}

const DEV_TOKEN_SNIPPET =
  "localStorage.setItem('canopy.dev.token', 'dev:00000000-0000-0000-0000-000000000001:00000000-0000-0000-0000-000000000010:ReportAuthor,ReportAdmin,TenantAdmin,ReportRunner,CopilotUser'); location.reload();";

/**
 * Shown when no session source produced a token. The dev-token snippet only
 * appears where the API accepts `dev:` tokens; in prod it was a dead end that
 * advertised a bypass which does not work there, so people opening
 * sage.gocanopy.io directly are sent to the portal instead.
 */
export function SessionUnavailable({ devTokens }: { readonly devTokens: boolean }): JSX.Element {
  return (
    <div className="auth-bootstrap">
      <div className="auth-bootstrap-card">
        <div className="row" style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <div className="brand-mark" aria-hidden="true">
            C
          </div>
          <div>
            <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Session unavailable</h2>
            <p className="text-dim text-sm" style={{ margin: '2px 0 0' }}>
              Canopy Sage couldn't resolve a token.
            </p>
          </div>
        </div>
        {devTokens ? (
          <>
            <p>
              No session token available. In dev, paste this into the browser console and reload:
            </p>
            <pre>{DEV_TOKEN_SNIPPET}</pre>
            <p style={{ marginTop: 'var(--space-3)' }}>
              Format: dev:&lt;tenantId&gt;:&lt;userId&gt;:&lt;roles&gt;. Both IDs must be real
              UUIDs; the seeded tenant/user above works out of the box.
            </p>
          </>
        ) : (
          <p>
            Open Sage from the Canopy portal: sign in to Canopy and go to Insights. Sage can't be
            opened on its own.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Whether the "Session unavailable" screen offers the dev-token snippet: on a
 * Vite dev build always (local work, where the API may simply not be up yet,
 * and a fail-closed health check would otherwise point at a portal that does
 * not exist locally), and on a deployed build only when the API says so.
 */
export async function offerDevTokenSnippet(): Promise<boolean> {
  return isDevBuild() || (await apiAllowsDevTokens());
}

/**
 * Whether this deployment's API accepts `dev:` tokens, from the auth-free
 * `GET /health` (`devTokens`). Fails closed: an unreachable API, an older API
 * without the field, or a slow answer all mean "no", so prod never shows the
 * snippet by accident.
 */
export async function apiAllowsDevTokens(timeoutMs = 3000): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    const res = await fetch(`${apiOrigin}/reporting/api/health`, { signal: controller.signal });
    if (!res.ok) return false;
    const body = (await res.json()) as { devTokens?: unknown } | null;
    return body?.devTokens === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fires once after the session is ready: fetches /me/tenants and
 * stashes the activeTenantId in the module-level store
 * (active-tenant.ts) that the QueryClient's hash function reads.
 * Then invalidates every cached query so any data fetched BEFORE
 * the active tenant resolved (with the `_` placeholder hash) gets
 * re-fetched under the correct tenant-scoped hash.
 *
 * Renders children immediately — we don't block paint on this
 * probe. A few early queries may fire under the placeholder hash
 * and then re-fetch milliseconds later; the user sees a brief
 * spinner at worst, never stale cross-tenant data.
 */
function ActiveTenantResolver({
  token,
  onToken,
  children,
}: {
  readonly token: string;
  /** CSAGE-387: hands a portal-pushed refreshed jwt back up to the session. */
  readonly onToken: (jwt: string) => void;
  readonly children: ReactNode;
}): JSX.Element {
  const qc = useQueryClient();
  useEffect(() => {
    let cancelled = false;
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    fetch(`${apiOrigin}/reporting/api/me/tenants`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { activeTenantId?: string } | null) => {
        if (cancelled || !data?.activeTenantId) return;
        // CSAGE-387: this effect is keyed on `token`, which NOW changes on every
        // refresh (~every 15 min). Re-probing with the fresh credential is
        // right; wiping the cache is not — an unconditional `removeQueries()`
        // here would drop every cached query on a cadence and refetch the whole
        // screen mid-session. Only a tenant that actually MOVED re-addresses the
        // cache, so only that case needs the eviction below.
        if (data.activeTenantId === getActiveTenantId()) return;
        // CSAGE-471: tear the cache down BEFORE the tenant address moves, and
        // cancel in-flight work first — the ordering the scope handler already
        // uses (CSAGE-396), which this call site was the last one missing. On a
        // cold embed open the first content queries (dashboards, folders,
        // reports) fire under the `_` placeholder-tenant hash and are routinely
        // still ON THE WIRE when this /me/tenants probe resolves. The old order
        // — `setActiveTenantId()` and THEN a bare `removeQueries()` — moved
        // every observer's cache address to the real tenant FIRST, so each
        // in-flight response then landed at the now-orphaned `_` address while
        // its observer sat on the new address with no fetch scheduled. That is
        // the "empty until I reload" first-open bug: the dashboards and folders
        // panels stayed blank until a manual refresh happened to resolve
        // /me/tenants before the content queries fired. Cancelling while the
        // address is still `_` kills those in-flight fetches, removing evicts
        // the `_` entries, and only then does the address move — so every
        // observer misses cleanly and refetches under the real tenant.
        //
        // CSAGE-364: REMOVE, not invalidate. `setActiveTenantId` changes what
        // `tenantScopedHashFn` returns for every key, so once it runs the
        // entries cached under the `_` placeholder are addressed by a hash no
        // observer will ever compute again. `invalidateQueries` only marks them
        // stale — it cannot evict them, and it leaves each mounted observer
        // attached to an entry that has been orphaned mid-flight. See
        // active-tenant.ts for why the hash reads mutable module state at all.
        void qc.cancelQueries();
        qc.removeQueries();
        setActiveTenantId(data.activeTenantId);
      })
      .catch(() => {
        /* AuthBootstrap already showed children; quietly do nothing */
      });
    return () => {
      cancelled = true;
    };
  }, [token, qc]);

  // Track the portal's org-tree selection for the lifetime of the embed.
  // The portal re-posts `canopy.auth` whenever the active org changes; when
  // the hierarchyPath moves, update the scope store and drop every cached
  // query so reports/dashboards re-fetch under the new customer. Gated on
  // isTrustedPortalMessage (CSAGE-314) — this listener is long-lived, so
  // the gate applies to every message it ever receives, not just the first.
  //
  // CSAGE-364: `removeQueries`, not `invalidateQueries` — same reasoning as the
  // tenant resolver above. The scope is the second segment of every cache key's
  // hash, so changing it re-addresses the whole cache; invalidating can only
  // mark the now-unreachable entries stale, which additionally means a
  // scope A → B → A round trip re-attaches to A's old entry and paints the
  // previous customer's data (or its cached ERROR) before the refetch lands.
  useEffect(() => {
    const onMessage = createPortalMessageHandler({
      onToken,
      // CSAGE-396: cancel THEN remove. `removeQueries` alone evicts cache
      // entries but does not stop a request already on the wire; that
      // response still resolves, into an address the scope change has
      // orphaned. Cancelling first means no in-flight fetch can deliver
      // across the boundary.
      resetQueries: () => {
        void qc.cancelQueries();
        qc.removeQueries();
      },
    });
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [qc, onToken]);

  return <>{children}</>;
}

/**
 * Portal message handler factory (CSAGE-314, design D6). Extracted out of the
 * `useEffect` above so it's directly testable without mounting React —
 * `ActiveTenantResolver` wires the factory to `adoptToken` + `qc.removeQueries`.
 * The `isTrustedPortalMessage` gate is the first statement, before
 * `event.data` is read at all.
 *
 * CSAGE-364 renamed the dependency from `invalidateQueries` to `resetQueries`:
 * marking stale is not sufficient once the scope has already changed, because
 * the scope is baked into every cache key's hash (see `tenantScopedHashFn`).
 * The name is part of the contract — a future caller wiring this to
 * `invalidateQueries` would silently reintroduce the bug.
 *
 * CSAGE-387 renamed the factory itself from `createScopeMessageHandler`, for
 * the same "the name is the contract" reason. It was never only about scope:
 * every `canopy.auth` the portal re-posts carries a live `jwt`, and this
 * handler — the only long-lived listener in the app — read `brandColor` and
 * `hierarchyPath` off those messages and threw the credential away. An
 * org-switch re-post carries a freshly-renewed jwt; Sage kept using the old
 * one. (The portal does not push on renewal, so the 401-triggered pull in
 * `client.ts` is what actually closes the expiry bug — see `adoptToken`.)
 */
export function createPortalMessageHandler(deps: {
  readonly onToken: (jwt: string) => void;
  readonly resetQueries: () => void;
}): (event: MessageEvent) => void {
  return (event: MessageEvent): void => {
    if (!isTrustedPortalMessage(event)) return;
    const data = event.data as PortalAuthMessage | null;
    if (!data || typeof data !== 'object' || data.type !== 'canopy.auth') return;
    // Brand may change with the customer context — re-apply every time.
    if (typeof data.brandColor === 'string')
      applyBrand(data.brandColor, data.brandTextColor ?? null);
    // CSAGE-387: adopt the credential BEFORE the hierarchyPath early-return.
    // Order is the whole fix — a pure refresh message has no scope change to
    // report, so anything downstream of that return never sees it.
    if (typeof data.jwt === 'string' && data.jwt) deps.onToken(data.jwt);
    if (typeof data.hierarchyPath !== 'string') return;
    const next = data.hierarchyPath.trim() || null;
    // CSAGE-387: an EMPTY hierarchyPath means "no information", not "no scope".
    // The portal sends `this.cookies.get('hierarchyPath') || ''`, so a missing
    // cookie reaches us as `''` on EVERY re-post — including the reply to our
    // own refresh request. Treating that as null would drop the customer's
    // selected node mid-session and wipe the cache, widening the view to the
    // whole entitled subtree (the server's ceiling still clamps it, so this is
    // wrong-scope, not a leak). Harmless while the token was frozen because no
    // refresh reply ever arrived; now that a 401 asks for one, it would fire.
    // A genuine clear only happens at bootstrap, where there is nothing to lose.
    if (next === null) return;
    if (next === getActiveScopePath()) return;
    // CSAGE-396: tear the cache down BEFORE the address moves, not after.
    //
    // `tenantScopedHashFn` reads `getActiveScopePath()` on every observer
    // read, so the instant this variable changes, every existing entry is
    // re-addressed. Doing that first left a window: a request already in
    // flight resolved into the OLD address while its subscriber had already
    // re-rendered onto the NEW one. The response was never seen by anyone
    // and the panel sat on "Loading…" forever — with the server logging a
    // clean 200, which is what made it so hard to place.
    //
    // Reversing the order closes the window: `resetQueries` cancels
    // in-flight work and evicts the cache while the address is still the one
    // those queries were issued under, so nothing can outlive the change.
    // Observers then miss and refetch under the new scope.
    deps.resetQueries();
    setActiveScopePath(next);
  };
}

/**
 * The validated origin of the first ACCEPTED `canopy.auth` message
 * (design D5) — recorded by `waitForPortalAuth`, read by
 * `requestPortalRefresh` as the one legitimate outbound target for a
 * refresh request. Set-once: a later accepted message (e.g. from a
 * second bootstrap wait) does not overwrite an already-recorded origin.
 */
let recordedPortalOrigin: string | null = null;

function recordPortalOrigin(origin: string): void {
  if (recordedPortalOrigin === null) recordedPortalOrigin = origin;
}

function buildSession(
  token: string,
  source: AuthSession['source'],
  onToken: (jwt: string) => void,
): AuthSession {
  return {
    token,
    source,
    refresh: async () => {
      // Real refresh path is only available when the portal embedded
      // Sage via postMessage — the parent window holds the JWT lifecycle.
      // URL-fragment and dev-token sources have no refresh; return the
      // token they were issued with (callers handle expiry themselves).
      if (source !== 'postMessage') return token;
      const next = await sharedPortalRefresh();
      // CSAGE-387: land it on the session as well as returning it, so the
      // caller's retry and every later request use the new credential. Without
      // this the refresh would fix one request and the session would keep
      // handing out the dead token.
      onToken(next);
      return next;
    },
  };
}

/**
 * CSAGE-387: single-flight wrapper around `requestPortalRefresh`.
 *
 * A token expires between requests, not during one, so an expiry typically
 * 401s several in-flight calls at once — a report view fires parameters,
 * schedules and run-history together. Each would otherwise post its own
 * `canopy.auth.refresh.request` to the portal and wait its own 5s. Sharing one
 * in-flight promise means one ask, one answer, and every waiter resolves on it.
 *
 * Deliberately not cached beyond the in-flight window: the next 401 after this
 * settles is new information (the fresh token has itself expired, or the portal
 * never answered) and must ask again.
 */
let inFlightRefresh: Promise<string> | null = null;

export function sharedPortalRefresh(timeoutMs = 5000): Promise<string> {
  // The first caller's timeout governs the shared attempt — joiners are asking
  // the same question of the same portal, so a second budget is meaningless.
  if (inFlightRefresh) return inFlightRefresh;
  const pending = requestPortalRefresh(timeoutMs).finally(() => {
    // Guard the identity check: a later refresh may already own the slot.
    if (inFlightRefresh === pending) inFlightRefresh = null;
  });
  inFlightRefresh = pending;
  return pending;
}

/**
 * Refresh handler (CSAGE-314, AuthBootstrap.tsx:232-245 originally,
 * design D5/D6). Extracted so it's directly testable. Posts the refresh
 * request ONLY to the recorded portal origin (never `'*'`) — if no
 * portal origin has been recorded yet, there is no legitimate window to
 * ask, so the refresh rejects immediately rather than broadcasting.
 *
 * A candidate reply that fails `isTrustedPortalMessage` does not resolve
 * the refresh and does NOT remove the listener — the timeout, not a
 * rejected reply, is what ends the wait.
 */
export function requestPortalRefresh(timeoutMs = 5000): Promise<string> {
  const targetOrigin = recordedPortalOrigin;
  if (!targetOrigin) {
    return Promise.reject(
      new Error('Cannot refresh: no trusted portal origin has been recorded yet'),
    );
  }
  return new Promise<string>((resolve, reject) => {
    const handler = (event: MessageEvent): void => {
      if (!isTrustedPortalMessage(event)) return;
      const data = event.data as PortalAuthMessage | null;
      if (
        data &&
        typeof data === 'object' &&
        data.type === 'canopy.auth' &&
        typeof data.jwt === 'string'
      ) {
        window.removeEventListener('message', handler);
        resolve(data.jwt);
      }
    };
    window.addEventListener('message', handler);
    try {
      window.parent?.postMessage({ type: 'canopy.auth.refresh.request' }, targetOrigin);
    } catch {
      /* not embedded; the timeout will fire */
    }
    setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('Portal did not respond to refresh request within 5s'));
    }, timeoutMs);
  });
}

interface PortalAuthMessage {
  readonly type: 'canopy.auth';
  readonly jwt: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly theme?: string;
  /** Live org-tree selection in the portal, e.g. `/Banyan/MoodMedia`.
   *  Drives the content scope (x-canopy-scope) server-side. */
  readonly hierarchyPath?: string;
  /** Customer brand colors mirrored from the portal's nav theme:
   *  brandColor = primarycolor (--branding-bg-color); brandTextColor =
   *  navtextcolor. Drive the embed tab buttons' branded fill. */
  readonly brandColor?: string;
  readonly brandTextColor?: string;
}

/**
 * Derives a candidate parent origin BEFORE any `canopy.auth` message has
 * arrived, for the readiness ping only (design D5). `ancestorOrigins` is
 * preferred — unspoofable and unaffected by referrer policy — with
 * `document.referrer` as the Firefox fallback (it lacks `ancestorOrigins`).
 * Returns null, never a fallback to `'*'`, when nothing can be derived.
 */
function deriveCandidateParentOrigin(): string | null {
  const ancestorOrigins = (
    window.location as unknown as {
      ancestorOrigins?: { readonly length: number; [i: number]: string };
    }
  ).ancestorOrigins;
  const fromAncestor = ancestorOrigins?.[0];
  if (typeof fromAncestor === 'string' && fromAncestor) return fromAncestor;

  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Bootstrap listener (CSAGE-314, originally AuthBootstrap.tsx:276-308).
 * Gated on `isTrustedPortalMessage` as the handler's FIRST statement,
 * before any read of `event.data` — an untrusted message can't even
 * trigger `applyBrand`. Timeout and fall-through behavior are unchanged:
 * a rejected message does not resolve the promise, so the bootstrap
 * keeps waiting for the remainder of `timeoutMs` and then falls through
 * to the URL-fragment and dev-token sources exactly as it does today
 * when no message arrives at all.
 *
 * The first ACCEPTED message's origin is recorded as the portal origin
 * (see `recordPortalOrigin`) for `requestPortalRefresh` to target.
 *
 * The outbound readiness ping no longer uses `targetOrigin: '*'`: it
 * posts only to a derived-and-validated parent origin (see
 * `deriveCandidateParentOrigin`), and is skipped entirely when no
 * candidate can be derived or the derived one isn't allow-listed (design
 * D5, R1) — the portal posts `canopy.auth` unprompted on iframe load
 * regardless, and the long-lived scope listener also catches it.
 */
export function waitForPortalAuth(timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    // The fall-through timer, cleared when a message arrives first. Without
    // this it stayed armed for the whole timeoutMs after auth had already
    // resolved: harmless in a browser (removeEventListener is idempotent and
    // the second resolve is a no-op on a settled promise), but under jsdom the
    // test environment can be torn down before it fires, and the callback then
    // touches a `window` that no longer exists. That surfaced as four
    // "ReferenceError: window is not defined" unhandled errors failing
    // `apps/web test` in CI while passing locally — a pure ordering race.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const handler = (event: MessageEvent): void => {
      if (!isTrustedPortalMessage(event)) return;
      const data = event.data as PortalAuthMessage | null;
      if (
        data &&
        typeof data === 'object' &&
        data.type === 'canopy.auth' &&
        typeof data.jwt === 'string'
      ) {
        window.removeEventListener('message', handler);
        if (timer !== undefined) clearTimeout(timer);
        recordPortalOrigin(event.origin);
        // Capture the selected org node before resolving so the very first
        // content queries already carry the scope.
        //
        // CSAGE-384: an EMPTY hierarchyPath is skipped, matching
        // createPortalMessageHandler. The portal sends
        // `cookies.get('hierarchyPath') || ''`, and an empty value here is not a
        // cosmetic problem: `getActiveScopePath()` null means `client.ts` omits
        // `x-canopy-scope` entirely, and for a portal SSO session that header is
        // the ONLY source of `ctx.scopePath`. The session then silently runs at
        // its entitlement ceiling — the ancestor — and any subscription created
        // in it recorded that wider scope and delivered the wrong data every
        // week. Writing null here rather than leaving the store alone would also
        // clobber a scope a previous message had already established.
        if (typeof data.hierarchyPath === 'string' && data.hierarchyPath.trim())
          setActiveScopePath(data.hierarchyPath);
        // Apply the customer brand to the embed chrome.
        if (typeof data.brandColor === 'string')
          applyBrand(data.brandColor, data.brandTextColor ?? null);
        resolve(data.jwt);
      }
    };
    window.addEventListener('message', handler);
    // Tell the parent we're ready to receive auth — only to a derived,
    // allow-listed origin; skipped (never '*') otherwise.
    const candidate = deriveCandidateParentOrigin();
    if (candidate && isAllowedPortalOrigin(candidate)) {
      try {
        window.parent?.postMessage({ type: 'canopy.auth.ready' }, candidate);
      } catch {
        // Not embedded — silent.
      }
    }
    timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, timeoutMs);
  });
}

function readFragmentToken(): string | null {
  const fragment = window.location.hash.replace(/^#/, '');
  if (!fragment) return null;
  const params = new URLSearchParams(fragment);
  return params.get('token');
}
